import { useMemo } from 'preact/hooks';
import { EDD_SOURCE } from '@pperform/pp-constants/parcel';
import { calculateDiffFromToday } from 'utils/datetime.utils';
import type Shipment from 'interfaces/PmParcel';
import { EddSource } from 'interfaces/edd.interface';
import {
  shouldShowEddByStage,
  shouldShowEddByEvents,
} from '../utils/eddValidation';

// Source-specific key lists — owned here, passed into the generic util
// H-phase is included: shouldShowEddByEvents checks ALL events in the list, whereas
// shouldShowEddByStage only checks the latest event's resulting stage. A parcel can have
// an H-phase event present before the stage transitions to delivered, so both gates are
// needed for full coverage.
// G30–G34 are needed: out_for_delivery stage can still show EDD when the flag allows it,
// but these specific sub-events warrant hiding regardless.
const CARRIER_EDD_PHASE_KEYS: string[] = ['H'];
const CARRIER_EDD_EVENT_KEYS = ['G30', 'G31', 'G32', 'G33', 'G34'];

interface UseEddVisibilityParams {
  eddValue: EddSource | null | undefined;
  parcel: Shipment;
  isDisplayEnabled: boolean;
  shouldShowForOutForDelivery: boolean;
}

export const useEddVisibility = ({
  eddValue,
  parcel,
  isDisplayEnabled,
  shouldShowForOutForDelivery,
}: UseEddVisibilityParams): boolean => {
  return useMemo(() => {
    // Shared gates (all sources)
    if (!isDisplayEnabled) return false;
    if (!eddValue) return false;
    if (!shouldShowEddByStage(parcel?.stage ?? '', shouldShowForOutForDelivery))
      return false;

    const dateToCheck = eddValue.last?.time || eddValue.first?.time;
    if (!dateToCheck) return false;

    // Carrier-specific rules
    if (eddValue.source === EDD_SOURCE.carrier) {
      if (
        !shouldShowEddByEvents({
          events: parcel?.events ?? [],
          phaseKeys: CARRIER_EDD_PHASE_KEYS,
          eventKeys: CARRIER_EDD_EVENT_KEYS,
        })
      )
        return false;
      // 24h grace window: carrier EDD only — confirmed by PO (ARTEMIS-6200)
      // Second granularity: exactly 24h still shows; 24h+1s hides.
      if (calculateDiffFromToday(dateToCheck, 'second') < -(24 * 3600))
        return false;
      return true;
    }

    // Fallback: if EDD in the past, do not show CUSTOMER_EDD, PREDICT_EDD
    if (calculateDiffFromToday(dateToCheck, 'second') < 0) return false;
    return true;
  }, [eddValue, parcel, isDisplayEnabled, shouldShowForOutForDelivery]);
};
